import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import clsx from 'clsx'
import { PlayCircleIcon, RefreshCwIcon } from 'lucide-react'
import {
  extractPlayableSolutionVideoUrl,
  extractYouTubeId,
} from '@quebon/shared-learning'
import { Button } from '@quebon/ui/components/button'
import { Badge } from '@quebon/ui/components/badge'
import {
  useUnitAssessmentDetail,
  useStartPrintUnitAssessment,
  useGradeUnitAssessmentItem,
  useCompleteUnitAssessment,
  useCancelUnitAssessment,
} from '@/hooks/coaching/useUnitAssessments'
import { LmsPrintPreviewPdfButton } from '@/components/admin/print/LmsPrintPreviewPdfButton'
import type { UaItemDetail } from '@/api/unitAssessmentAdmin'
import { coachingKeys } from '@/hooks/coaching/useCoachingToday'
import { usePersistedGradingMode } from '@/hooks/coaching/usePersistedGradingMode'
import { gradingDraftScope, useGradingDraft } from '@/hooks/coaching/useGradingDraft'
import { useAuthStore } from '@/store/authStore'
import { hasLcPermission } from '@/utils/roleUtils'
import { parseStoredAnswer } from './component-details/parts/answerArray'
import { QuestionGradingList, type QuestionGradingItem } from './QuestionGradingList'
import {
  UnitAssessmentSolutionVideoDialog,
  type UnitAssessmentSolutionVideo,
} from './UnitAssessmentSolutionVideoDialog'
import { UnitAssessmentPlacementDialog } from './UnitAssessmentPlacementDialog'
import { GradingModeSwitch } from './GradingModeSwitch'
import { UnitAssessmentGradingSheet } from './grading-sheet/UnitAssessmentGradingSheet'
import type { QuizFocusRequest } from './grading-sheet/coachingFocus'

interface Props {
  learnerId: number
  unitAssessmentId: number
  readOnly?: boolean
  /** 취소/삭제 후 트리 선택 해제. */
  onCleared?: () => void
}

function toQuestionGradingItem(item: UaItemDetail): QuestionGradingItem {
  return {
    quizId: item.quizId,
    gradedIsCorrect: item.gradedIsCorrect,
    studentAnswer: parseStoredAnswer(item.answer),
    corrects: item.corrects,
    questionSnapshot: item.questionSnapshot,
    correctAnswer: item.correctAnswer,
    explanation: item.explanation,
  }
}

/**
 * 단원평가 특강 교사 채점/완료 영역.
 * PRINT/ONLINE: 채점표 O/X 일괄 저장 또는 코칭에서 답안 수정·자동재채점 → 완료/리뷰 오픈.
 */
export function UnitAssessmentCoachingDetail({ learnerId, unitAssessmentId, readOnly = false, onCleared }: Props) {
  const { t } = useTranslation(['coaching', 'common'])
  const qc = useQueryClient()
  const user = useAuthStore(s => s.user)
  const canEdit = !readOnly && hasLcPermission(user ?? undefined, 'COACHING')
  const { mode: gradingMode, setMode: setGradingMode } = usePersistedGradingMode()
  const { draft, setDraft } = useGradingDraft(gradingDraftScope.unitAssessment(learnerId, unitAssessmentId))
  const [focusRequest, setFocusRequest] = useState<QuizFocusRequest | null>(null)
  const [sheetSaving, setSheetSaving] = useState(false)
  const [solutionVideo, setSolutionVideo] = useState<UnitAssessmentSolutionVideo | null>(null)
  const [placementOpen, setPlacementOpen] = useState(false)
  useEffect(() => { if (readOnly) setPlacementOpen(false) }, [readOnly])
  const detail = useUnitAssessmentDetail(learnerId, unitAssessmentId)
  const startPrint = useStartPrintUnitAssessment(learnerId, unitAssessmentId)
  const grade = useGradeUnitAssessmentItem(learnerId, unitAssessmentId)
  const complete = useCompleteUnitAssessment(learnerId, unitAssessmentId)
  const cancel = useCancelUnitAssessment(learnerId)
  useEffect(() => {
    setFocusRequest(null)
    setSolutionVideo(null)
    setPlacementOpen(false)
  }, [learnerId, unitAssessmentId])
  useEffect(() => {
    if (detail.data?.status === 'COMPLETED' || detail.data?.status === 'CANCELLED') {
      setDraft(() => ({}))
    }
  }, [detail.data?.status, setDraft])

  if (detail.isLoading) {
    return (
      <section className="rounded-lg border border-gray-200 p-6" role="status" aria-live="polite">
        <p className="text-sm text-gray-500">{t('unitAssessment.loading')}</p>
      </section>
    )
  }
  if (detail.error || !detail.data) {
    return (
      <section className="rounded-lg border border-gray-200 p-6" role="alert">
        <p className="text-sm text-red-600">{t('unitAssessment.loadError')}</p>
      </section>
    )
  }

  const data = detail.data
  const isPrint = data.mode === 'PRINT'
  const status = data.status
  const items = [...data.items].sort((a, b) => a.sortOrder - b.sortOrder)
  const submitting = sheetSaving || grade.isPending || startPrint.isPending || complete.isPending || cancel.isPending
  const dirty = items.some(item => draft[item.quizId] !== undefined && draft[item.quizId] !== (item.gradedIsCorrect == null ? null : item.gradedIsCorrect ? 1 : 0))
  const answerEditable = canEdit && (status === 'STARTED' || status === 'SUBMITTED' || status === 'GRADED' || status === 'REVIEW_OPEN')
  const canStartPrint = canEdit && isPrint && status === 'PENDING' && !dirty && !submitting
  const canComplete = canEdit && !dirty && !submitting && items.length > 0 && items.every(item => item.gradedIsCorrect != null) && (status === 'GRADED' || status === 'REVIEW_OPEN')
  const done = status === 'COMPLETED'
  const canPreviewSolutionVideo =
    status === 'STARTED' ||
    status === 'SUBMITTED' ||
    status === 'GRADED' ||
    status === 'REVIEW_OPEN' ||
    status === 'COMPLETED'
  const completeLabel = status === 'GRADED' ? t('unitAssessment.openReview') : t('unitAssessment.complete')

  const statusKey =
    status === 'COMPLETED'
      ? 'completed'
      : status === 'REVIEW_OPEN'
        ? 'reviewOpen'
        : status === 'GRADED'
          ? 'graded'
          : status === 'SUBMITTED'
            ? 'submitted'
            : status === 'STARTED'
              ? 'started'
              : status === 'CANCELLED'
                ? 'cancelled'
                : 'pending'

  const onCancel = async () => {
    if (!canEdit || submitting || dirty) return
    if (!window.confirm(t('unitAssessment.cancelConfirm'))) return
    await cancel.mutateAsync(unitAssessmentId)
    onCleared?.()
  }
  const requestCoaching = (quizId: string) => {
    if (submitting) return
    setGradingMode('coaching')
    setFocusRequest({ quizId, nonce: Date.now() })
  }
  const saveSheetGrade = (quizId: string, isCorrect: boolean) => {
    setSheetSaving(true)
    return grade.mutateAsync({ quizId, overrideIsCorrect: isCorrect })
  }
  const handleSheetSaveSettled = () => {
    setSheetSaving(false)
    void qc.invalidateQueries({ queryKey: coachingKeys.unitAssessments(learnerId) })
  }

  return (
    <section className="flex flex-col gap-3" data-testid="ua-coaching-detail">
      <section className="rounded-lg border border-border bg-card">
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted px-4 py-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground"><ContentMetadataText text={data.setName || t('unitAssessment.title')} /></h2>
            <Badge variant="outline">
              {t(`unitAssessment.mode.${data.mode}`)}
            </Badge>
            <Badge
              data-testid="ua-status"
              variant={done ? 'default' : 'outline'}
            >
              {t(`unitAssessment.statusLabel.${statusKey}`)}
            </Badge>
            {data.score != null && (
              <span className="text-xs font-medium text-muted-foreground">{t('unitAssessment.score', { score: data.score })}</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <GradingModeSwitch mode={gradingMode} onChange={setGradingMode} disabled={submitting} />
            {!isPrint && gradingMode === 'grading' ? (
              <Button
                variant="outline"
                size="sm"
                data-testid="ua-refresh-btn"
                onClick={() => void detail.refetch()}
                disabled={submitting || dirty || detail.isFetching}
              >
                <RefreshCwIcon data-icon="inline-start" className={clsx(detail.isFetching && 'animate-spin')} />
                {t('button.refresh', { ns: 'common' })}
              </Button>
            ) : null}
            {status === 'PENDING' && canEdit && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="ua-change-placement-btn"
                onClick={() => setPlacementOpen(true)}
                disabled={submitting || dirty}
              >
                {t('unitAssessment.change')}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="ua-print-btn"
              onClick={() =>
                window.open(
                  `/admin/coaching/print/${learnerId}/${data.anchorLessonId}/unit-assessment?uaId=${data.id}`,
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            >
              {t('unitAssessment.printQuestions')}
            </Button>
            <LmsPrintPreviewPdfButton
              href={`/admin/coaching/print/${learnerId}/${data.anchorLessonId}/unit-assessment?uaId=${data.id}`}
            />
            {isPrint && status === 'PENDING' && canEdit && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="ua-start-print-btn"
                onClick={() => startPrint.mutate()}
                disabled={!canStartPrint}
              >
                {startPrint.isPending ? t('unitAssessment.starting') : t('unitAssessment.startPrint')}
              </Button>
            )}
            {!done && status !== 'CANCELLED' && (
              <Button
                type="button"
                size="sm"
                data-testid="ua-complete-btn"
                onClick={() => complete.mutate()}
                disabled={!canComplete}
                title={!canComplete ? t('unitAssessment.completeHint') : undefined}
              >
                {complete.isPending ? t('unitAssessment.completing') : completeLabel}
              </Button>
            )}
            {!done && status !== 'CANCELLED' && canEdit && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="ua-cancel-btn"
                onClick={onCancel}
                disabled={submitting || dirty}
              >
                {t('unitAssessment.cancel')}
              </Button>
            )}
          </div>
        </header>

        {!isPrint && status === 'PENDING' ? (
          <div
            data-testid="ua-online-pending-guide"
            role="status"
            aria-live="polite"
            className="m-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
          >
            <p className="text-sm font-semibold text-amber-900">
              {t('unitAssessment.onlinePendingTitle')}
            </p>
            <p className="mt-1 text-xs leading-5 text-amber-800">
              {t('unitAssessment.onlinePendingHint')}
            </p>
          </div>
        ) : null}

        {data.items.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">{t('unitAssessment.empty')}</p>
        ) : isPrint && status === 'PENDING' ? (
          <p className="p-6 text-sm text-gray-500">{t('unitAssessment.startPrintHint')}</p>
        ) : gradingMode === 'grading' ? (
          <div className="p-4">
            <UnitAssessmentGradingSheet
              key={`${learnerId}:${unitAssessmentId}`}
              assessment={data}
              learnerId={learnerId}
              canGrade={answerEditable && !startPrint.isPending && !complete.isPending && !cancel.isPending}
              saveGrade={saveSheetGrade}
              onSaveSettled={handleSheetSaveSettled}
              onRequestCoaching={requestCoaching}
            />
          </div>
        ) : (
          <QuestionGradingList
            key={`${learnerId}:${unitAssessmentId}`}
            presentation="single"
            items={items.map(toQuestionGradingItem)}
            canGrade={answerEditable}
            isSubmitting={submitting}
            initialSelectedQuizId={focusRequest?.quizId ?? null}
            focusRequest={focusRequest}
            resultHideSolveDescription
            navigationActions={!isPrint ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="ua-refresh-btn"
                onClick={() => void detail.refetch()}
                disabled={submitting || dirty || detail.isFetching}
              >
                <RefreshCwIcon
                  data-icon="inline-start"
                  className={clsx(detail.isFetching && 'animate-spin')}
                />
                {t('button.refresh', { ns: 'common' })}
              </Button>
            ) : undefined}
            renderItemActions={(_, index) => {
              const item = items[index]
              const solutionVideoUrl = canPreviewSolutionVideo
                ? extractPlayableSolutionVideoUrl(item.explanation)
                : null
              const solutionVideoId = extractYouTubeId(item.explanation)
              return solutionVideoUrl ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="ua-solution-video-btn"
                  onClick={() =>
                    setSolutionVideo({
                      itemNo: index + 1,
                      playbackUrl: solutionVideoUrl,
                      originalUrl: solutionVideoId
                        ? `https://www.youtube.com/watch?v=${solutionVideoId}`
                        : solutionVideoUrl,
                    })
                  }
                >
                  <PlayCircleIcon data-icon="inline-start" />
                  {t('unitAssessment.solutionVideo.open')}
                </Button>
              ) : null
            }}
            onGrade={async ({ quizId, answers, isCorrect }) => {
              const updated = await grade.mutateAsync({
                quizId,
                answer: JSON.stringify(answers),
                overrideIsCorrect: isCorrect,
              })
              await qc.invalidateQueries({ queryKey: coachingKeys.unitAssessments(learnerId) })
              return updated
            }}
          />
        )}
      </section>
      <UnitAssessmentSolutionVideoDialog
        video={solutionVideo}
        onClose={() => setSolutionVideo(null)}
      />
      {placementOpen && !readOnly ? (
        <UnitAssessmentPlacementDialog
          learnerId={learnerId}
          unitAssessmentId={unitAssessmentId}
          initialAnchorLessonId={data.anchorLessonId}
          onClose={() => setPlacementOpen(false)}
        />
      ) : null}
    </section>
  )
}
